import React from "react";
import { useExternalSupplierShipmentsControllerFindMallOrder } from "@semo/api-contracts";
import { Truck, Package, CheckCircle2, XCircle } from "@semo/icons";

interface MallOrderTrackingSectionProps {
  /** 견적요청 UUID. 공고번호로는 부르지 않는다 — 서버가 UUID 축으로 주문을 찾는다. */
  quoteRequestId: string;
}

/** 몰 주문 상태 표기. 서버 enum 을 그대로 보여 주면 「지금 누구를 기다리나」가 안 읽힌다. */
const ORDER_STATUS_LABEL: Record<string, string> = {
  PLACED: "주문 접수",
  QUOTING: "견적 받는 중",
  QUOTE_CLOSED: "견적 마감",
  SENT_TO_ERP: "구매팀 전달됨",
  CONTRACTED: "계약 체결",
  SHIPPING: "배송중",
  DELIVERED: "배송완료",
  INSPECTED: "검수완료",
  SETTLED: "정산완료",
  CANCELED: "취소",
};

const SHIPMENT_STATUS_LABEL: Record<string, string> = {
  READY: "준비중",
  SHIPPED: "출고",
  IN_TRANSIT: "배송중",
  DELIVERED: "배송완료",
  CANCELED: "취소",
};

const day = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString("ko-KR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : null;

/**
 * 몰 주문의 **배송 현황** — 견적 상세에 붙는다.
 *
 * 발주기관이 「내가 요청한 물건이 어디쯤인지」를 보는 자리다. 이것이 없으면 씨마켓
 * 거래관리에서는 계약까지만 보이고, 그 뒤는 몰에 다시 들어가야 알 수 있었다.
 *
 * 몰에서 올라온 공고가 아니면 서버가 404 로 답하고, 그때는 **아무것도 그리지 않는다** —
 * 일반 견적 공고 화면에 「배송 없음」 칸이 생기면 안 된다.
 */
export const MallOrderTrackingSection: React.FC<
  MallOrderTrackingSectionProps
> = ({ quoteRequestId }) => {
  const { data: response, isLoading } =
    useExternalSupplierShipmentsControllerFindMallOrder(quoteRequestId, {
      query: {
        enabled: !!quoteRequestId,
        // 몰 주문이 아닌 공고가 대부분이다. 404 를 재시도하면 그 화면마다 요청이 세 번 난다.
        retry: false,
      },
    });

  const order = response?.data;
  if (isLoading || !order) return null;

  const shipments = order.shipments;

  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <header className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <Truck className="w-4 h-4 text-slate-500" />
          배송 현황
        </h2>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-mono">{order.orderNo}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
            {ORDER_STATUS_LABEL[order.status] ?? order.status}
          </span>
          {order.erpRequisitionNo && (
            <span>구매요구 {order.erpRequisitionNo}</span>
          )}
        </div>
      </header>

      {shipments.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
          아직 등록된 송장이 없습니다.
        </p>
      ) : (
        <ul className="space-y-3">
          {shipments.map((shipment) => {
            const canceled = shipment.status === "CANCELED";
            const delivered = shipment.status === "DELIVERED";
            return (
              <li
                key={shipment.id}
                className={`rounded-lg border p-3 ${
                  canceled
                    ? "border-slate-200 bg-slate-50 opacity-60"
                    : "border-slate-200"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {canceled ? (
                    <XCircle className="w-4 h-4 text-slate-400" />
                  ) : delivered ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Package className="w-4 h-4 text-primary-text" />
                  )}
                  <span className="font-medium text-slate-900">
                    {SHIPMENT_STATUS_LABEL[shipment.status] ?? shipment.status}
                  </span>
                  {shipment.courierName && (
                    <span className="text-slate-600">
                      {shipment.courierName}
                    </span>
                  )}
                  {shipment.trackingNo && (
                    <span className="font-mono text-xs text-slate-500">
                      {shipment.trackingNo}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-slate-500">
                    {day(shipment.deliveredAt ?? shipment.shippedAt) ?? ""}
                  </span>
                </div>

                <p className="mt-1.5 text-xs text-slate-600">
                  {shipment.items
                    .map((item) => `${item.name} ${item.quantity}`)
                    .join(" · ")}
                </p>

                {shipment.note && (
                  <p className="mt-1 text-xs text-slate-500">{shipment.note}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
